/* Dar de alta un artículo de Stock desde el buscador de un movimiento, y leer
 * el tablero de herramientas para ofrecer lo que ya está ahí (Nico, 20/10).
 * La regla —qué se ofrece y con qué datos— vive en `shared/stockDesdeTablero`;
 * acá está sólo lo que toca la base.
 */
import { supabase } from "../supabase/SupabaseClient";
import { createRow } from "./supabaseRealtime";
import { realtime } from "./store";
import { faltaColumna } from "./herramientas";
import { enriquecerArticulos } from "../shared/stock";
import { stockTypeOptions } from "../components/formularios/data/OptionsContent";
import {
  faltaEnBorrador, filaDeArticulo, PROVEEDOR_GENERICO, proximoCodigo,
} from "../shared/stockDesdeTablero";

/* «NO PUDE LEER» NO ES «NO HAY NINGUNA». Si el tablero no se pudo leer, la
 * lista de lo que se ofrece sale vacía igual que si no hubiera nada, y la
 * pantalla lo tiene que poder decir. Misma regla que `services/herramientas`. */
export async function herramientasDelTablero() {
  const [h, p] = await Promise.all([
    supabase
      .from("taller_herramienta")
      .select("id, codigo, nombre, panol, categoria, dias_prestamo, activo")
      .eq("activo", true)
      .order("nombre", { ascending: true }),
    supabase.from("panoles").select("id, nombre"),
  ]);
  if (h.error) {
    console.warn("[stockAlta] no pude leer el tablero:", h.error.message);
    return { filas: [], panoles: {}, error: h.error.message };
  }
  /* Sin los nombres de los pañoles se muestra el id: es feo, no es falso. */
  const panoles = Object.fromEntries(
    (p.error ? [] : p.data || []).map((x) => [x.id, x.nombre]),
  );
  return { filas: h.data || [], panoles, error: "" };
}

/* Los tipos de Stock como se guardan («INSUMO», «HERRAMIENTA»…), con su
 * prefijo de dos letras. */
export const TIPOS_DE_STOCK = Object.entries(stockTypeOptions()).map(
  ([prefijo, t]) => ({ prefijo, tipo: t.tipo }),
);

/* Con la migración aplicada, una herramienta tiene UNA sola ficha
 * (`stock_una_ficha_por_herramienta`). Si ése es el índice que salta, no es
 * un choque de códigos: alguien —FlotaBot, otra pestaña— ya le creó el
 * artículo mientras el formulario estaba abierto, y probar otro código choca
 * igual las cuatro veces. */
const yaTieneFicha = (error) =>
  /stock_una_ficha_por_herramienta/.test(
    `${error?.message || ""} ${error?.details || ""}`,
  );

const esDuplicado = (error) =>
  String(error?.code || "") === "23505" ||
  /duplicate key|already exists|23505/i.test(String(error?.message || ""));

/* Una vez que la base dijo que no tiene `stock.herramienta_id`, no se vuelve
 * a preguntar en esta sesión. */
let sinVinculo = false;

/* Crea el artículo. `{ok: true, articulo}` o `{error}`.
 *
 * EL CÓDIGO SE LEE DE LA BASE JUSTO ANTES DE ESCRIBIR, no de la lista en
 * memoria: FlotaBot da de alta artículos por WhatsApp y desde la página del
 * pañol con el mismo prefijo, y la lista de acá puede tener minutos. Si dos
 * caen en el mismo código, la base contesta duplicado y se prueba el que
 * sigue — lo mismo que hace `panol_stock.dar_de_alta`.
 *
 * SIN LA COLUMNA DEL VÍNCULO TAMBIÉN SE CREA. La migración que agrega
 * `stock.herramienta_id` la aplica el dueño; hasta entonces el artículo se
 * guarda sin él y la herramienta se reconoce por el nombre. Que una columna
 * que todavía no está impida cargar sería peor que no tenerla. */
export async function crearArticulo(borrador) {
  const tipos = TIPOS_DE_STOCK.map((t) => t.tipo);
  const falta = faltaEnBorrador(borrador, tipos);
  if (falta) return { error: falta };
  const prefijo =
    TIPOS_DE_STOCK.find((t) => t.tipo === borrador.tipo).prefijo + PROVEEDOR_GENERICO;

  const probados = [];
  for (let intento = 0; intento < 4; intento += 1) {
    const { data, error } = await supabase
      .from("stock")
      .select("id")
      .like("id", `${prefijo}%`);
    if (error) {
      console.warn("[stockAlta] no pude leer los códigos:", error.message);
      return { error: "No se pudo consultar Stock. Probá de nuevo en un rato." };
    }
    const codigo = proximoCodigo(prefijo, [...(data || []).map((x) => x.id), ...probados]);
    const fila = filaDeArticulo(codigo, borrador, { conVinculo: !sinVinculo });
    try {
      const creada = await createRow("stock", codigo, fila);
      /* Con el nombre del proveedor, como los que vienen de la lista: el
         formulario lo rotula igual que a los demás. */
      const [articulo] = enriquecerArticulos(
        [{ ...fila, ...creada, id: codigo }],
        realtime.proveedores || [],
      );
      sumarALaLista(articulo);
      return { ok: true, articulo };
    } catch (e) {
      if (!sinVinculo && faltaColumna(e, "herramienta_id")) {
        sinVinculo = true;
        intento -= 1; // no fue un choque de códigos: el mismo intento, sin el vínculo
        continue;
      }
      if (yaTieneFicha(e)) {
        return {
          error:
            "Esa herramienta ya tiene su artículo en Stock (lo acaban de crear). Cerrá y volvé a abrir para elegirlo.",
        };
      }
      if (esDuplicado(e)) {
        probados.push(codigo);
        continue;
      }
      console.warn("[stockAlta] no pude crear el artículo:", e?.message || e);
      return {
        error: /permission|policy|42501|row-level/i.test(String(e?.message || ""))
          ? "No tenés permiso para dar de alta artículos en Stock. Pedí que te habiliten la carga del pañol."
          : "No se pudo crear el artículo. Probá de nuevo en un rato.",
      };
    }
  }
  return { error: "Otro estaba dando de alta artículos al mismo tiempo. Probá de nuevo." };
}

/* El movimiento que se guarda a continuación (`submitMovimientoStock`) busca
 * el artículo en la lista en memoria para sumarle al total. El aviso del
 * alta por tiempo real puede tardar un poco más que el «Guardar», y sin esto
 * el total del artículo quedaría en cero aunque el depósito sí se cargue. */
function sumarALaLista(articulo) {
  const lista = realtime.stock || [];
  if (lista.some((a) => String(a.id) === String(articulo.id))) return;
  realtime.stock = [...lista, articulo];
}

/* Sólo para los tests: vuelve a preguntar por la columna del vínculo. */
export const _olvidarVinculo = () => {
  sinVinculo = false;
};
